(() => {
  'use strict';

  if (customElements.get('order-protection')) return;

  const shopRoot = `${window.Shopify?.routes?.root || '/'}`.replace(/\/?$/, '/');
  const endpoints = {
    cart: `${shopRoot}cart.js`,
    add: `${shopRoot}cart/add.js`,
    update: `${shopRoot}cart/update.js`
  };

  let orphanCleanupRunning = false;

  async function requestJson(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      ...options,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(options.headers || {})
      }
    });

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(
        data?.description || data?.message || 'The cart could not be updated.'
      );
    }

    return data;
  }

  function fetchCart() {
    return requestJson(endpoints.cart, { method: 'GET' });
  }

  function refreshNativeDrawer(cart) {
    document.dispatchEvent(
      new CustomEvent('order-protection:changed', {
        bubbles: true,
        detail: { cart }
      })
    );

    /* The current Purty theme listens for this and redraws cart-drawer/cart-bubble. */
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  class OrderProtection extends HTMLElement {
    connectedCallback() {
      this.variantId = Number(this.dataset.variantId);
      this.checkbox = this.querySelector('[data-order-protection-toggle]');
      this.status = this.querySelector('[data-order-protection-status]');

      if (!this.variantId || !this.checkbox || !this.status) return;

      this.handleChange = this.handleChange.bind(this);
      this.addEventListener('change', this.handleChange);
      this.sync();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.handleChange);
    }

    protectionItems(cart) {
      return cart.items.filter((item) => Number(item.variant_id) === this.variantId);
    }

    merchandiseItems(cart) {
      return cart.items.filter((item) => Number(item.variant_id) !== this.variantId);
    }

    setBusy(isBusy, message = '') {
      this.toggleAttribute('aria-busy', isBusy);
      this.checkbox.disabled = isBusy;

      if (message) {
        this.status.dataset.error = 'false';
        this.status.textContent = message;
      } else if (this.status.dataset.error !== 'true') {
        this.status.textContent = '';
      }
    }

    showError(error) {
      this.status.dataset.error = 'true';
      this.status.textContent = error?.message || 'Please try again.';
    }

    markProtectionCartLines() {
      const prefix = `${this.variantId}:`;

      document
        .querySelectorAll('#Cart-Drawer quantity-selector[data-id]')
        .forEach((quantitySelector) => {
          if (quantitySelector.dataset.id.startsWith(prefix)) {
            quantitySelector
              .closest('.product-cart-item')
              ?.classList.add('order-protection-cart-line');
          }
        });
    }

    async removeItems(items) {
      if (!items.length) return null;

      const updates = {};
      items.forEach((item) => {
        updates[item.key] = 0;
      });

      return requestJson(endpoints.update, {
        method: 'POST',
        body: JSON.stringify({ updates })
      });
    }

    async normaliseToOne(items) {
      if (!items.length) return null;

      const needsNormalising =
        items.length > 1 || Number(items[0].quantity) !== 1;

      if (!needsNormalising) return null;

      const updates = {};
      items.forEach((item, index) => {
        updates[item.key] = index === 0 ? 1 : 0;
      });

      return requestJson(endpoints.update, {
        method: 'POST',
        body: JSON.stringify({ updates })
      });
    }

    async sync() {
      const syncId = Symbol('order-protection-sync');
      this.syncId = syncId;

      try {
        const cart = await fetchCart();
        if (!this.isConnected || this.syncId !== syncId) return;

        const protectionItems = this.protectionItems(cart);
        const hasMerchandise = this.merchandiseItems(cart).length > 0;

        this.checkbox.checked = protectionItems.length > 0;
        this.hidden = !hasMerchandise;
        this.markProtectionCartLines();

        /* Never leave protection as the only product in the cart. */
        if (!hasMerchandise && protectionItems.length && !orphanCleanupRunning) {
          orphanCleanupRunning = true;

          try {
            const updatedCart = await this.removeItems(protectionItems);
            refreshNativeDrawer(updatedCart);
          } finally {
            orphanCleanupRunning = false;
          }
        }
      } catch (error) {
        if (this.isConnected) this.showError(error);
      }
    }

    async handleChange(event) {
      if (!event.target.matches('[data-order-protection-toggle]')) return;

      const shouldAdd = event.target.checked;
      this.setBusy(
        true,
        shouldAdd ? 'Adding protection…' : 'Removing protection…'
      );

      try {
        const cart = await fetchCart();
        const protectionItems = this.protectionItems(cart);

        let updatedCart;

        if (shouldAdd) {
          if (protectionItems.length) {
            updatedCart = await this.normaliseToOne(protectionItems);
          } else {
            await requestJson(endpoints.add, {
              method: 'POST',
              body: JSON.stringify({
                items: [
                  {
                    id: this.variantId,
                    quantity: 1,
                    properties: {
                      _order_protection: 'Added by customer'
                    }
                  }
                ]
              })
            });
          }
        } else {
          updatedCart = await this.removeItems(protectionItems);
        }

        if (!updatedCart) updatedCart = await fetchCart();

        this.checkbox.checked = shouldAdd;
        this.status.dataset.error = 'false';
        this.status.textContent = shouldAdd ? 'Order protected' : '';
        refreshNativeDrawer(updatedCart);
      } catch (error) {
        this.checkbox.checked = !shouldAdd;
        this.showError(error);
      } finally {
        if (this.isConnected) this.setBusy(false);
      }
    }
  }

  customElements.define('order-protection', OrderProtection);

  function syncVisibleProtectionControls() {
    document.querySelectorAll('order-protection').forEach((element) => {
      element.sync?.();
    });
  }

  document.addEventListener('cart-drawer:open', syncVisibleProtectionControls);
  document.addEventListener('cart:item-added', syncVisibleProtectionControls);
  document.addEventListener('line-item:change:end', syncVisibleProtectionControls);
})();
